import { ChangeDetectorRef, Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { plainToInstance } from 'class-transformer';
import { SettingsEntry } from '../../components/pages/settings.page';
import { TranslateFormatText } from '../../helpers/array.helpers';
import { InjectorHelper } from '../../helpers/injector.helper';
import { PermissionHelper } from '../../helpers/permissions.helper';
import { DataModel } from '../../models/datamodel/datamodel.model';
import { TranslatedString } from '../../models/translatedstring.model';
import { DataModelService } from '../../services/datamodel.service';
import { BaseListDetail, BaseListSettings, DeleteTexts, SaveTexts } from '../base.list.settings';
import { DataObjectSettings } from './dataobject/dataobject.settings';
import { DataSourceSettings } from './datasource/datasource.settings';
import { ContainerEnumSettings } from './enum/containerenum.settings';
import { NavigationService } from '../../services/navigation.service';

@Component({
    selector: 'datamodel-settings',
    templateUrl: '../base.list.settings.html'
})
export class DataModelSettings extends BaseListSettings {

    constructor(private dataService: DataModelService, private translate: TranslateService, protected cdRef: ChangeDetectorRef) {
        super(cdRef);
        this.ShowDelete = PermissionHelper.IsAdmin();
    }

    getContentType() {
        return DataModelDetail;
    }

    loadList(handler) {
        this.dataService.GetModels().subscribe(models => {
            if (models) {
                const list = [];
                models.forEach(function (model) {
                    list.push({
                        Caption: model.Caption,
                        ID: model.SID,
                        IsCapsule: model.IsCapsule,
                        IsOverridden: model.IsOverridden
                    });
                });
                handler(list);
            }
        });
    }

    loadData(data) {
        this.dataService.GetDataModel(data).subscribe(dataModel => {
            if (dataModel) {
                const dm = plainToInstance(DataModel, dataModel);
                this.setSelectedItem(dm);
            }
        });
    }

    getNewItem() {
        const dm = new DataModel();
        dm.Name = 'DataModel_' + this.ListItems.length + 1;
        dm.Caption = new TranslatedString(this.translate.instant('@@Neues DataModel'));
        return dm;
    }

    getDeleteText(sel): DeleteTexts {
        const retVal = new DeleteTexts();
        retVal.Question = new TranslateFormatText('@@Sind Sie sicher, dass Sie das DataModel \'{0}\' loeschen moechten?');
        retVal.Question.FormatParams.push(sel.Caption);
        retVal.Success = new TranslateFormatText('@@DataModel \'{0}\' erfolgreich geloescht.');
        retVal.Success.FormatParams.push(sel.Caption);
        retVal.Title = new TranslateFormatText('@@DataModel loeschen');
        return retVal;
    }

    getSaveSuccessText(sel): SaveTexts {
        let caption = TranslatedString.GetTranslation(sel.Caption);
        if (!caption) {
            caption = sel.Name;
        }
        const retVal = new SaveTexts();
        retVal.Text = new TranslateFormatText('@@DataModel \'{0}\' erfolgreich gespeichert.');
        retVal.Text.FormatParams.push(caption);
        retVal.Title = new TranslateFormatText('@@DataModel speichern');
        return retVal;
    }

    delete(data, handler) {
        this.dataService.DeleteModel(data).subscribe((res) => {
            handler(res);
            NavigationService.Refresh.next(DataModelSettings._ModelNode.ID);
        });
    }

    saveInternal(item, handler) {
        this.dataService.SaveModel(item).subscribe(result => {
            if (result) {
                handler(result, result.SID, result.Caption);
            }
        });
    }

    handleNew(item, result) {
        if (!item.SID) {
            item.SID = result.SID;
        }
        if (!item.Version) {
            item.Version = result.Version;
        }
        NavigationService.Refresh.next(DataModelSettings._ModelNode.ID);
    }

    updateListItem(item, result) {
        item.IsCapsule = result.IsCapsule;
        item.IsOverridden = result.IsOverridden;
    }

    private static _ModelNode: SettingsEntry = {
        Caption: '@@DataModel',
        ID: 'dataModel',
        Icon: 'table2',
        Index: 0,
        Parent: 'data',
        Security: {
            Name: 'evidanza.App.Shared.Security.DataRight',
            Value: 2
        },
        Children: [],
        UpdateFunction: DataModelSettings.UpdateModelNodes
    };

    static GetSettingsEntry(): SettingsEntry {
        return DataModelSettings._ModelNode;
    }

    static UpdateModelNodes() {
        return new Promise<void>(resolve => {
            DataModelSettings._ModelNode.Children = [];
            const ds = InjectorHelper.InjectorInstance.get<DataModelService>(DataModelService);
            ds.GetModels().subscribe(models => {
                const list = [];
                list.push({
                    Caption: '@@Uebersicht',
                    ID: 'dm-overview',
                    Icon: 'panels-top-left',
                    Index: 0,
                    Content: DataModelSettings
                });
                if (models) {
                    models.forEach((model: any, i: number) => {
                        const node = DataModelSettings.CreateModelNode(model);
                        node['Index'] = i + 1;
                        list.push(node);
                    });
                }
                DataModelSettings._ModelNode.Children = list;
                resolve();
            });
        });
    }

    private static CreateModelNode(model: any) {
        const retVal = {
            Caption: model.Caption,
            ID: model.SID,
            Icon: 'grid-2x2-plus',
            Security: {
                Name: 'evidanza.App.Shared.Security.DataRight',
                Value: 2
            },
            Children: [
                {
                    Caption: '@@Datenquellen',
                    ID: 'datasources',
                    Icon: 'server',
                    Index: 0,
                    Security: {
                        Name: 'evidanza.App.Shared.Security.DataRight',
                        Value: 4
                    },
                    Content: DataSourceSettings,
                    InitArgs: model
                },
                {
                    Caption: '@@Datenstruktur',
                    ID: 'datastructure',
                    Security: {
                        Name: 'evidanza.App.Shared.Security.DataRight',
                        Value: 8
                    },
                    Icon: 'network',
                    Index: 1,
                    Children: [
                        {
                            Caption: '@@Datenobjekte',
                            ID: 'dataobjects',
                            Icon: 'file-box',
                            Index: 0,
                            Security: {
                                Name: 'evidanza.App.Shared.Security.DataRight',
                                Value: 4096
                            },
                            Content: DataObjectSettings,
                            InitArgs: model
                        },
                        {
                            Caption: '@@Enums',
                            ID: 'enums',
                            Icon: 'list2',
                            Index: 1,
                            Security: {
                                Name: 'evidanza.App.Shared.Security.DataRight',
                                Value: 8192
                            },
                            Content: ContainerEnumSettings,
                            InitArgs: model
                        }
                    ]
                }
            ]
        };
        DataModelRegistry.Registry.forEach((child, i) => {
            const regEntry = DataModelSettings.GetRegEntry(model, child);
            regEntry.Index = i + 2;
            retVal.Children.push(regEntry);
        });
        return retVal;
    }

    private static GetRegEntry(model, entry) {
        const retVal = Object.assign({}, entry);
        retVal.ID = entry.ID;
        if (entry.Children) {
            retVal.Children = [];
            entry.Children.forEach((child, i) => {
                const regEntry = DataModelSettings.GetRegEntry(model, child);
                regEntry.Index = i;
                retVal.Children.push(regEntry);
            });
        } else {
            retVal.InitArgs = model;
        }
        return retVal;
    }
}

@Component({
    selector: 'datamodel-detail',
    templateUrl: './datamodel.settings.html',
    styleUrls: ['./datamodel.settings.css']
})
export class DataModelDetail extends BaseListDetail {
    Initialize(InitArgs) {
        if (InitArgs) {
            this.SelectedItem = InitArgs.Data;
        }
    }
    GetDialogResult() {
        return this.SelectedItem;
    }
}

export class DataModelRegistry {
    static Registry = [];
}
